import { useState, useRef, KeyboardEvent } from "react";
import { EMAIL_REGEX } from "utils/Common/Constant";
import Input from "Components/Common/Input/Input";
import "./ChipEmailInput.scss";

interface Props {
  value: string[] | string;
  onChange: (value: string[]) => void;
  onErrorChange?: (hasError: boolean) => void;
  onInputTextChange?: (text: string) => void;
  placeholder?: string;
  className?: string;
}

const ChipEmailInput = ({
  value,
  onChange,
  onErrorChange,
  onInputTextChange,
  placeholder = "Type email and press Enter to add",
  className = "",
}: Props) => {
  const chips: string[] = Array.isArray(value)
    ? value
    : typeof value === "string" && value
      ? (value as string).split(",").filter(Boolean)
      : [];

  const [inputText, setInputText] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const addChip = () => {
    const email = inputText.trim();
    if (!email) return;

    if (!EMAIL_REGEX.test(email)) {
      setError("Invalid email address");
      return;
    }
    if (chips.includes(email)) {
      setError("Email already added");
      return;
    }

    onChange([...chips, email]);
    setInputText("");
    onInputTextChange?.("");
    setError("");
    onErrorChange?.(false);
  };

  const removeChip = (email: string) => {
    onChange(chips.filter((c) => c !== email));
  };

  const handleChange = (text: string) => {
    setInputText(text);
    onInputTextChange?.(text);
    if (!text.trim()) {
      setError("");
      onErrorChange?.(false);
      return;
    }
    const invalid = !EMAIL_REGEX.test(text.trim());
    setError(invalid ? "Invalid email address" : "");
    onErrorChange?.(invalid);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addChip();
    }
    if (e.key === "Backspace" && !inputText && chips.length) {
      removeChip(chips[chips.length - 1]);
    }
  };

  return (
    <div className="overflow-auto">
      <div className={`chip-email-wrap ${className}`}>
        <div
          ref={scrollRef}
          className={`chip-email-inner${error ? " chip-email-error" : ""}`}
          onClick={() => inputRef.current?.focus()}
        >
          {chips.map((email) => (
            <span key={email} className="chip">
              {email}
              <button
                type="button"
                className="chip-remove"
                onMouseDown={(e) => {
                  e.preventDefault();
                  removeChip(email);
                }}
              >
                ×
              </button>
            </span>
          ))}
          <Input
            ref={inputRef}
            type="text"
            className="chip-input"
            value={inputText}
            placeholder={chips.length === 0 ? placeholder : ""}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />
        </div>
        {error && <span className="chip-error">{error}</span>}
      </div>
    </div>
  );
};

export default ChipEmailInput;
